/**
 * Der Aufruf der Pruefseite — app-lokal, nicht im geteilten Package.
 *
 * 🔴 **Zwei vorhandene Endpunkte hintereinander, kein eigener.**
 *   1. `POST /api/v1/invoice/parse` — *was ist das?* Erkennt Format und Land
 *      und liest die Rechnungsdaten heraus.
 *   2. `POST /api/v1/invoice/{cc}/{format}/validate` — *stimmt es?* Prueft
 *      gegen XSD und Schematron.
 *
 * Beide Schritte bekommen **dieselbe Datei** (`{data, filename}`): `/parse`
 * liefert Land und Format fuer den Pfad, `/validate` prueft die Datei selbst
 * gegen XSD und Regelwerk (KW-003, Plan API 2026-10-09/01, IA-BE ≥ 2.1.0).
 * Jeder Schritt bucht eine `parse`-Einheit — ein Dokument kostet also zwei
 * (Torsten Nr. 180 = A); der Hinweis auf der Seite sagt das.
 *
 * ⚠️ Am 2026-10-03 stand hier kurz ein eigener Endpunkt
 * (`/validate-document`): ein vierter Pruefpfad neben drei vorhandenen.
 * Zurueckgebaut (Backend-Revert `8aa3021b`). Heute nimmt der vorhandene Pfad
 * die Datei als zweite Eingabeform.
 *
 * ⚪ Warum hier und nicht in `useInvoiceRestClient`: der Client liegt in
 * `@xhubio-saas/invoice-frontend`, und die Pruefseite ist invoice-api-eigen.
 * Eine Methode dort hiesse ein Paket-Release mehr fuer eine Flaeche, die nur
 * diese App hat. (Stand 2026-10-10 pinnt die App die aktuelle 3.52.0 — die
 * fruehere Begruendung „zwei Minor zurueck" gilt nicht mehr.)
 */

/** Was der Server ueber das abgelegte Dokument herausgefunden hat. */
export interface Detection {
  format: string | null
  formatVersion?: string
  countryCode: string | null
  confidence: number
  formatMethod?: string
  countrySource?: string
  isAmbiguous?: boolean
  alternativeCountries?: string[]
}

/** Ein Befund, eine Warnung oder ein Hinweis. */
export interface Finding {
  code: string
  message: string
  /** Die Norm-Kennung (`BR-DE-15`), wenn die Antwort eine nennt. */
  rule?: string
  /** Der Feldpfad im Dokument, wenn bekannt. */
  field?: string
  /** Warnphase: heute eine Warnung, ab diesem Datum eine Absage. */
  enforceFrom?: string
}

/**
 * Der Hinweis-Code, mit dem der Parser sagt, dass er das XML aus einem PDF
 * geholt hat. Es gibt dafuer **kein eigenes Feld** in der Antwort von
 * `/parse` — nur diesen Eintrag in `infos[]`
 * (`invoice-backend/.../pdf-embedded.ts`, `PDF_EXTRACTION_INFO_CODE`).
 */
export const PDF_INFO_CODE = 'EXTRACTED_FROM_PDF'

/** Das Urteil der Pruefung, wie `/validate` es faellt. */
export type ValidationStatus = 'passed' | 'failed' | 'not_run'

/** Was der Aufrufer bekommt — beide Schritte zusammengefuehrt. */
export interface ValidationResult {
  /** HTTP-Status des letzten Aufrufs. */
  status: number
  /**
   * Schritt 1: Liess sich die Datei lesen? `false` heisst, dass Schritt 2 gar
   * nicht erst stattgefunden hat.
   */
  wasRead: boolean
  /** Format und Land, die der Server aus den Bytes gelesen hat. */
  detection?: Detection
  /** `true`, wenn das XML aus einem abgelegten PDF stammt (aus `infos[]`). */
  fromPdf?: boolean
  /**
   * Das Rechnungsobjekt, das `/parse` aus der Datei gelesen hat (`invoice` der Antwort), unveraendert
   * durchgereicht. Nur gesetzt, wenn Schritt 1 gelang — bricht der Lauf beim Lesen ab, fehlt es.
   *
   * ⚪ Ein eingebettetes XML gibt es daneben NICHT: `/parse` liefert bei einem PDF nur den Hinweis
   * `EXTRACTED_FROM_PDF` in `infos[]` (`autoParseResponseSchema`, gemessen am Backend 2026-10-10).
   */
  parsed?: unknown
  /**
   * Schritt 2: das Urteil der Regelpruefung. `undefined`, solange nicht
   * geprueft wurde — 🔴 nicht mit `not_run` verwechseln: das heisst „geprueft
   * werden sollte, ging aber nicht" und ist eine Aussage der API.
   */
  verdict?: ValidationStatus
  /** Nur bei `not_run`: warum kein Regelwerk lief. */
  reason?: 'missing' | 'load-failed' | 'transform-failed' | 'not-bound'
  /** Die Befunde der Regelpruefung (Schritt 2) bzw. des Lesens (Schritt 1). */
  findings: Finding[]
  /** Warnungen aus beiden Schritten. */
  warnings: Finding[]
  /** Welches Format geprueft wurde — die Spec-Id aus der Antwort. */
  checkedFormat?: string
  /**
   * Nur bei der Handauswahl: das Katalog-Token, das der Nutzer gewaehlt hat (`zugferd`). Die Seite schlaegt damit
   * im Katalog (`GET /formats`) nach, welche Fassungen das Token traegt — die Version kommt aus der API, nicht von
   * hier. Bei der Erkennung fehlt das Feld: dort sprechen `detection.formatVersion` und `rulebooks`.
   */
  selectedFormat?: string
  /** Die Regelwerke, nach denen geprueft wurde (`results[].format`). */
  rulebooks?: string[]
  /** Regelwerke, die fuer dieses Dokument nicht liefen, mit Grund (`results[].unappliedRulebooks`). */
  skippedRulebooks?: { rulebook: string | null; reason: string }[]
  /**
   * Je Regelwerk das eigene Urteil und die eigenen Befunde (`results[]`) — die Seite gruppiert ihren Befund-Report
   * danach. Fehlt `results[]` in der Antwort, fehlt das Feld: die Seite zeigt dann die flache Liste `findings`.
   */
  rulebookResults?: RulebookResult[]
  /**
   * 🔴 Gesetzt, wenn der Lauf als Ganzes nicht stattgefunden hat:
   *  · `notDetected`   400 `FORMAT_NOT_DETECTED` / `COUNTRY_NOT_DETECTED`.
   *  · `notValidatable`  Schritt 2 nicht moeglich: fuer Land und Format gibt es
   *                     keinen Pruefpfad.
   *  · `formatMismatch` 400 `FORMAT_MISMATCH`: die Datei ist ein anderes
   *                     Format/Land als der Pfad; der Servertext nennt beide.
   *  · `noKey` 401, kein gueltiger Schluessel.
   *  · `auth`           403, dem Schluessel fehlt ein Recht.
   *  · `quota`          429, Kontingent erschoepft.
   *  · `unavailable`    503, ein Regelwerk lief nicht.
   *  · `request`        400 ohne eigenen Code.
   */
  abort?: {
    kind:
      | 'notDetected'
      | 'notValidatable'
      | 'formatMismatch'
      | 'quota'
      | 'unavailable'
      | 'request'
      | 'noKey'
      | 'auth'
      | 'unknown'
    message: string
    /** In welchem Schritt es endete — die Seite sagt das dem Nutzer. */
    step: 'read' | 'validate'
  }
}

/**
 * Das Urteil EINES Regelwerks (`results[]` von `/validate`).
 *
 * 🔴 Drei Lagen, nie zwei: `valid` ist `true` (lief, nichts gefunden), `false` (lief, fand etwas) oder `null` (lief
 * nicht) — und `skipped` (`not entitled`) heisst ebenfalls „nicht geprueft". Ein Regelwerk, das nicht lief, darf
 * auf der Seite nie wie „keine Befunde" aussehen.
 */
export interface RulebookResult {
  /** Die Spec-Id des Regelwerks (`results[].format`, z. B. `extended-ctc-fr-ubl`). */
  rulebook: string
  valid: boolean | null
  /** Die Befunde dieses Regelwerks — `ruleId` der Antwort als `code`, wie in der flachen Liste. */
  findings: Finding[]
  /** Nur gesetzt, wenn das Regelwerk bewusst nicht lief (`not entitled`). */
  skipped?: string
}

type Execute = (request: {
  method: string
  url: string
  headers: Record<string, string>
  body?: string
}) => Promise<Response>

/** Die eigenen 400-Codes der beiden Routen auf die Abbruch-Arten der Seite. */
const CODE_TO_KIND: Record<string, NonNullable<ValidationResult['abort']>['kind']> = {
  FORMAT_NOT_DETECTED: 'notDetected',
  COUNTRY_NOT_DETECTED: 'notDetected',
  FORMAT_NOT_VALIDATABLE: 'notValidatable',
  FORMAT_MISMATCH: 'formatMismatch',
}

function abortKind(
  status: number,
  errorCode?: string
): NonNullable<ValidationResult['abort']>['kind'] {
  const fromCode = errorCode ? CODE_TO_KIND[errorCode] : undefined
  if (fromCode) return fromCode
  if (status === 429) return 'quota'
  // 🔴 401 und 403 sind zwei Lagen, nicht eine.
  if (status === 401) return 'noKey'
  if (status === 403) return 'auth'
  if (status === 503) return 'unavailable'
  if (status === 400) return 'request'
  return 'unknown'
}

/** Die Form, die `/parse` zurueckgibt (`autoParseResponseSchema`). */
interface ParseBody {
  success?: boolean
  /** Die gelesene Rechnung — `invoice` in `autoParseResponseSchema` (beide `/parse`-Routen). */
  invoice?: unknown
  detection?: Detection
  errors?: Finding[]
  warnings?: Finding[]
  infos?: { code: string; message: string }[]
  format?: string
  message?: string
  error?: ErrorField
  hint?: Hint
}

/** Die Form von `/{cc}/{format}/validate` (`formatValidationSchema`). */
interface ValidateBody {
  valid?: boolean | null
  status?: ValidationStatus
  reason?: ValidationResult['reason']
  format?: string
  errors?: Finding[]
  warnings?: Finding[]
  /** Je geprueftem Regelwerk ein Eintrag; `unappliedRulebooks` nennt, was nicht lief. */
  results?: {
    format: string
    valid?: boolean | null
    /** 🔴 Je Regelwerk heisst die Kennung `ruleId` — die flache Liste `errors[]` nennt sie `code`. */
    errors?: { ruleId?: string; code?: string; rule?: string; field?: string; message: string }[]
    skipped?: string
    unappliedRulebooks?: { rulebook: string | null; reason: string }[]
  }[]
  /** Die Datei war ein PDF, geprueft wurde das eingebettete XML. */
  extractedFromPdf?: boolean
  message?: string
  error?: ErrorField
  hint?: Hint
}

/**
 * `error` ist meist ein Code (`FORMAT_MISMATCH`), aber die Eingabepruefung beider Routen (`ohneEcho` im IA-BE) schickt
 * die zod-Liste — der Satz dafuer steht dann im `hint` (`INVOICE_MISSING` …).
 */
type ErrorField = string | unknown[]
interface Hint {
  code?: string
  message?: string
}

/**
 * Code und Meldung eines Abbruchs — 🔴 die Meldung ist immer ein Text (D12): eine Liste als React-Kind laesst die
 * Seite abstuerzen. Reihenfolge: `message`, der Code, der `hint`-Satz, der Status.
 */
function errorFrom(
  responseBody: { error?: ErrorField; message?: string; hint?: Hint } | null,
  status: number
): { code?: string; message: string } {
  const code =
    typeof responseBody?.error === 'string' ? responseBody.error : responseBody?.hint?.code
  const text = (w: unknown) => (typeof w === 'string' && w.length > 0 ? w : undefined)
  const message =
    text(responseBody?.message) ??
    text(typeof responseBody?.error === 'string' ? responseBody.error : undefined) ??
    text(responseBody?.hint?.message) ??
    `HTTP ${status}`
  return { code, message }
}

function isFromPdf(infos?: { code: string }[]): boolean {
  return (infos ?? []).some((i) => i.code === PDF_INFO_CODE)
}

/**
 * Erkanntes Format (Parser-Vokabular) → moegliche Katalog-Token.
 *
 * 🔴 Abgeschrieben aus `CANDIDATE_API_FORMATS`
 * (`invoice-frontend/.../lib/detect-format.ts`), weil das Paket sie nicht
 * exportiert — nur die drei Einstiegspunkte `plugin-invoice`,
 * `plugin-invoice-api`, `plugin-log-invoice` sind offen. Das Duplikat ist
 * bewusst und eng: `tests/api/invoiceValidationClient.test.ts` haelt es an
 * dieselben Faelle. Sobald das Paket die Tabelle exportiert, faellt diese
 * Kopie ersatzlos weg.
 *
 * ⚪ Nur Eintraege, bei denen der Name NICHT schon das Token ist — alles
 * andere erledigt der Praefix-Abgleich unten (`FATTURAPA` → `fatturapa`).
 */
const CANDIDATES: Record<string, readonly string[]> = {
  // 🔴 Die Zwitter MUESSEN hier stehen, obwohl der Name das Token enthaelt:
  // Der Praefix-Abgleich sucht im Katalog des Landes, und `facturx` steht im
  // deutschen Katalog nicht. Ohne diese Zeilen faende er nichts und die
  // Zwitter-Regel kaeme nie zum Zug (Test K-Zwitter).
  FACTUR_X_MINIMUM: ['facturx'],
  FACTUR_X_BASIC: ['facturx'],
  FACTUR_X_EN16931: ['facturx'],
  FACTUR_X_EXTENDED: ['facturx'],
  ZUGFERD_MINIMUM: ['zugferd'],
  ZUGFERD_BASIC: ['zugferd'],
  ZUGFERD_COMFORT: ['zugferd'],
  ZUGFERD_EN16931: ['zugferd'],
  ZUGFERD_EXTENDED: ['zugferd'],

  // Spanien: VeriFactu-XML liest der Katalog ueber den Facturae-Slot.
  VERIFACTU: ['facturae'],
  // Nationale UBL-Slots: das Land traegt den Slot, nicht das Format.
  KSEF: ['ubl'],
  KSEF_FA2: ['ubl'],
  KSEF_FA3: ['ubl'],
  SAFT_PT: ['ubl'],
  EFACTURA_RO: ['ubl', 'peppol-ubl'],
  PEPPOL_BIS: ['peppol-ubl', 'ubl'],
  UBL: ['ubl', 'peppol-ubl'],
  UBL_21: ['ubl', 'peppol-ubl'],
  NAV_XML: ['nav'],
  // Generisches CII ohne Profilkennung ist ZUGFeRD, Factur-X ODER die
  // CII-Auspraegung der XRechnung. Der Katalog des Landes entscheidet.
  CII: ['zugferd', 'facturx', 'xrechnung'],
}

/**
 * Die Zwitterformate: **dasselbe XML**, zwei nationale Namen.
 *
 * 🔴 Der Fall, an dem die erste Fassung scheiterte (im Browser gesehen,
 * 2026-10-04): Eine Factur-X-Rechnung, die diese Konsole selbst erzeugt hat,
 * wurde als „kein Prüfpfad" abgewiesen. Der Parser meldet
 * `FACTUR_X_EN16931`, der deutsche Katalog fuehrt aber `zugferd` — seit
 * ZUGFeRD 2.1 ist die Nutzlast identisch, nur der Name folgt dem Land.
 * Das Backend sagt es so: „FR/BE → `facturx`, everyone else (DE, CH, AT, …)
 * → `zugferd`" (`src/api/invoice.ts:465`), und `HYBRID_FORMATS` im
 * Katalog (`country-format-catalog.ts:324`) ist genau `['facturx','zugferd']`.
 *
 * ⚪ Deshalb wird hier nicht das Land abgefragt, sondern **beide Namen** als
 * Kandidaten angeboten — welcher gilt, sagt der Katalog des Landes.
 */
const HYBRID_FORMATS = ['facturx', 'zugferd'] as const

/**
 * Vom erkannten Format zum Katalog-Token, das in den Pfad gehoert.
 *
 * Drei Schritte, und der Katalog hat in allen das letzte Wort:
 *  1. Kandidaten aus der Tabelle, sonst Praefix-Abgleich (`XRECHNUNG_UBL` →
 *     `xrechnung`, weil `xrechnungubl` mit `xrechnung` beginnt).
 *  2. Ist ein Zwitter dabei, kommt sein Geschwister dazu.
 *  3. Gefiltert wird gegen die Formate, die das LAND wirklich fuehrt.
 *
 * Bleibt nichts uebrig, gibt die Funktion `undefined` — dann bietet die Seite
 * die Handauswahl an, statt einen Pfad zu raten.
 */
export function catalogToken(
  detectedFormat: string | null | undefined,
  formats: readonly { code: string }[]
): string | undefined {
  if (!detectedFormat) return undefined
  const available = formats.map((f) => f.code.toLowerCase())
  const flatten = (w: string) => w.toLowerCase().replace(/[^a-z0-9]/g, '')
  const detectedFlat = flatten(detectedFormat)

  const fromTable = CANDIDATES[detectedFormat.toUpperCase()] ?? []
  // Praefix-Abgleich: deckt alles ab, wo der Name schon das Token enthaelt.
  const fromName = available
    .filter((code) => detectedFlat.startsWith(flatten(code)))
    // Das laengste Token gewinnt: `xrechnung` vor `x`, wenn beides passt.
    .sort((a, b) => b.length - a.length)

  const candidates = [...fromTable, ...fromName]
  // Zwitter-Geschwister ergaenzen — dasselbe XML unter anderem Namen.
  const withHybrids = candidates.flatMap((k) =>
    HYBRID_FORMATS.includes(k as (typeof HYBRID_FORMATS)[number])
      ? [k, ...HYBRID_FORMATS.filter((z) => z !== k)]
      : [k]
  )

  return withHybrids.find((k) => available.includes(k))
}

/**
 * Der Anzeigename des Formats aus dem Katalog (`GET /formats`, Feld `label`) — statt des Parser-Tokens
 * (`XRECHNUNG_UBL`, `FACTUR_X_EN16931`).
 *
 * 🔴 Dieselbe Zuordnung wie der Pruefpfad: das Token kommt aus `catalogToken` (bzw. bei der Handauswahl aus der
 * Auswahl selbst), das Label aus dem Katalog-Eintrag dieses Tokens im Land. Kein zweites Woerterbuch im Browser.
 * Findet der Katalog nichts, gibt die Funktion `undefined` — die Seite zeigt dann das Token, statt einen Namen
 * zu erfinden.
 *
 * ⚪ Bei Zwittern traegt das Label das Spektrum der Fassungen (`ZUGFeRD 2.3/2.4/2.5`) — das ist die Auskunft des
 * Katalogs, nicht die Fassung des Dokuments. Die steht daneben (`detection.formatVersion`) oder ausdruecklich als
 * unbekannt. Ist sie bekannt, kuerzt `formatDisplayName` die Spanne weg (Hallmark-Audit 2026-10-10, Punkt 2).
 *
 * 🔴 Die Karte ruft das nicht direkt, sondern `formatDisplayName`: bei generischer Erkennung (`UBL`, `CII`) nennt das
 * Label den Pruefpfad, nicht die Datei.
 */
export function catalogLabel(
  detectedFormat: string | null | undefined,
  selectedFormat: string | undefined,
  formats: readonly { code: string; label: string }[]
): string | undefined {
  const token = selectedFormat ?? catalogToken(detectedFormat, formats)
  if (!token) return undefined
  return formats.find((f) => f.code.toLowerCase() === token.toLowerCase())?.label
}

/**
 * Die generischen Erkennungen des Backends (`InboundFormat` in
 * `invoice-backend/.../lib-invoice-inbound-interface/types.ts`): erkannt nur am Namespace, ohne Profil. Der Wert ist
 * der Name, den die Karte dafuer zeigt.
 *
 * 🔴 Eine FR-CTC-extended-UBL-Datei (`CustomizationID …#conformant#urn.cpro.gouv.fr:1p0:extended-ctc-fr`) kommt als
 * `UBL_21` an — ihre Kennung steht in keiner Tabelle der Erkennung. Der Pruefpfad landet trotzdem bei `ubl`, und
 * dessen Katalog-Label „UBL (Peppol BIS 3.0)" behauptete ein Profil, das die Datei nicht traegt (Patrick 2026-10-10).
 * Ebenso generisches `CII` → `zugferd` → „ZUGFeRD 2.3/2.4/2.5".
 */
const GENERIC_FORMAT_NAMES: Readonly<Record<string, string>> = {
  UBL: 'UBL',
  UBL_21: 'UBL',
  CII: 'CII',
}

/**
 * Die Versionsspanne am Ende eines Katalog-Labels: `<Name> <v>/<v>[/<v>…]`, jede Fassung aus Ziffern und Punkten.
 *
 * 🔴 Eng begrenzt: nur am Label-Ende, nur mit mindestens einem `/`. `FatturaPA 1.2.3` (eine Fassung),
 * `ZUGFeRD (CH)` und `UBL (Peppol BIS 3.0 / NL-CIUS)` (Klammer am Ende) bleiben unberuehrt. Der Katalog
 * (`GET /formats`: `code`, `label`, `kind`, `parse`) liefert keinen Basisnamen — sonst stuende er hier.
 */
const TRAILING_VERSION_RANGE = /^(.*\S)\s+\d+(?:\.\d+)*(?:\/\d+(?:\.\d+)*)+$/

/** Das Label ohne Versionsspanne am Ende; ohne Spanne das Label unveraendert. */
function withoutVersionRange(label: string): string {
  return TRAILING_VERSION_RANGE.exec(label)?.[1] ?? label
}

/**
 * Der Name, den die Erkennungskarte fuer das Format der Datei zeigt — nur, was belegt ist.
 *
 *  · Handauswahl: das Katalog-Label des gewaehlten Tokens (der Nutzer hat es so benannt) — MIT Spanne.
 *  · Erkennung generisch (`UBL`, `UBL_21`, `CII`): der generische Name, nie das Label des Pruefpfads.
 *  · Erkennung spezifisch (`XRECHNUNG_UBL`, `FACTUR_X_*`, `ZUGFERD_*`, `PEPPOL_BIS`, `EFACTURA_RO`, …): das
 *    Katalog-Label ueber `catalogLabel` — die Erkennung hat das Profil aus der Datei gelesen.
 *
 * 🔴 Ist die Fassung der Datei bekannt (`formatVersion`), faellt die Versionsspanne des Labels weg: die Karte nennt
 * sonst zwei Versionen nebeneinander („XRechnung 3.0/3.0.2 · Version 3.0.2", Hallmark-Audit 2026-10-10, Punkt 2).
 *
 * `undefined` heisst: kein Name belegt, die Seite zeigt das Token.
 */
export function formatDisplayName(
  detectedFormat: string | null | undefined,
  selectedFormat: string | undefined,
  formats: readonly { code: string; label: string }[],
  formatVersion?: string
): string | undefined {
  if (selectedFormat) return catalogLabel(undefined, selectedFormat, formats)
  if (!detectedFormat) return undefined
  const generic = GENERIC_FORMAT_NAMES[detectedFormat.toUpperCase()]
  if (generic) return generic
  const label = catalogLabel(detectedFormat, undefined, formats)
  return label && formatVersion ? withoutVersionRange(label) : label
}

/**
 * Profilname je Token-Suffix der Erkennung (`ZUGFERD_<PROFIL>`, `FACTUR_X_<PROFIL>`; geschlossene Menge aus
 * invoice-backend `lib-invoice-inbound-interface/types.ts`). Die Namen stehen in invoice-spec `doc/format-detection.md`
 * §3 (ProfileID → Profil); BASIC fehlt dort noch, `COMFORT` ist der ZUGFeRD-Name von EN 16931.
 *
 * 🔴 Nur der Name, kein Urteil „E-Rechnung ja/nein" — das ist ein Rechtswert und gehoert in die Spec (ENTSCHEID observer
 * 9766, Issue invoice-spec zur maschinenlesbaren Profiltabelle).
 */
const PROFILE_NAMES: Readonly<Record<string, string>> = {
  MINIMUM: 'MINIMUM',
  BASIC_WL: 'BASIC WL',
  BASIC: 'BASIC',
  COMFORT: 'EN 16931 (COMFORT)',
  EN16931: 'EN 16931 (COMFORT)',
  EXTENDED: 'EXTENDED',
}

const PROFILE_TOKEN = /^(?:ZUGFERD|FACTUR_X)_(.+)$/

/**
 * Das Profil, das die Erkennung aus der Datei gelesen hat — die API liefert kein Profilfeld, es steckt nur im Token.
 * Kein ZUGFeRD-/Factur-X-Token oder unbekanntes Suffix: `undefined` (nichts geraten).
 */
export function formatProfile(detectedFormat: string | null | undefined): string | undefined {
  const suffix = detectedFormat ? PROFILE_TOKEN.exec(detectedFormat.toUpperCase())?.[1] : undefined
  return suffix ? PROFILE_NAMES[suffix] : undefined
}

/** Das erste Element mit diesem lokalen Namen, gleich unter welchem Namespace-Praefix. */
function firstByLocalName(scope: Document | Element, localName: string): Element | undefined {
  return scope.getElementsByTagNameNS('*', localName)[0] ?? undefined
}

/**
 * Die Spezifikationskennung, die die Datei selbst traegt — gelesen im Browser aus dem schon dekodierten XML.
 *
 *  · UBL: `cbc:CustomizationID`.
 *  · CII: `ram:GuidelineSpecifiedDocumentContextParameter/ram:ID`.
 *
 * Namespace-tolerant ueber den lokalen Namen (Praefixe sind frei waehlbar). Kein XML, kaputtes XML, keine Kennung
 * oder kein `DOMParser`: `undefined` — die Karte laesst die Zeile dann weg.
 */
export function documentIdentifier(xml: string): string | undefined {
  if (typeof DOMParser === 'undefined') return undefined
  let doc: Document
  try {
    doc = new DOMParser().parseFromString(xml, 'application/xml')
  } catch {
    return undefined
  }
  if (doc.getElementsByTagName('parsererror').length > 0) return undefined
  const guideline = firstByLocalName(doc, 'GuidelineSpecifiedDocumentContextParameter')
  const node =
    firstByLocalName(doc, 'CustomizationID') ??
    (guideline ? firstByLocalName(guideline, 'ID') : undefined)
  const text = node?.textContent?.trim()
  return text ? text : undefined
}

/** Ein Befund mit der Zahl, wie oft er gemeldet wurde. */
export interface GroupedFinding {
  finding: Finding
  count: number
}

/**
 * Gleiche Befunde zusammenfassen: gleiche Kennung (`rule` bzw. `code`) UND gleicher Text = eine Zeile.
 *
 * ⚪ `/validate` prueft gegen mehrere Regelwerke (`en16931-ubl`, `extended-ctc-fr-ubl`) und meldet eine Regel,
 * die in beiden steht, zweimal — wortgleich. Die Liste `errors[]` nennt das Regelwerk je Eintrag nicht; darum
 * steht die Zahl („2×") statt einer Regelwerk-Liste. Reihenfolge: erstes Auftreten.
 */
export function groupFindings(findings: readonly Finding[]): GroupedFinding[] {
  const groups = new Map<string, GroupedFinding>()
  for (const finding of findings) {
    const key = `${finding.rule ?? finding.code}\u0000${finding.message}`
    const group = groups.get(key)
    if (group) group.count++
    else groups.set(key, { finding, count: 1 })
  }
  return [...groups.values()]
}

/**
 * Schritt 1 und 2 in einem Aufruf: lesen, dann pruefen.
 *
 * `countryCode`/`format` kommen von der Handauswahl und ueberspringen die
 * Erkennung. `formatsFor` liefert die Katalog-Formate eines Landes — daraus
 * entsteht das Token fuer den Pruefpfad.
 */
export async function parseAndValidate({
  execute,
  baseUrl,
  apiKey,
  fileBase64,
  fileName,
  countryCode,
  format,
  formatsFor,
}: {
  execute: Execute
  baseUrl: string
  apiKey: string
  fileBase64: string
  fileName?: string
  countryCode?: string
  format?: string
  formatsFor: (country: string) => readonly { code: string }[]
}): Promise<ValidationResult> {
  const requestHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${apiKey}`,
  }

  // ---- Schritt 1: was ist das? ------------------------------------------
  const manual = Boolean(countryCode && format)
  const selection = manual && format ? { selectedFormat: format } : {}
  const parsePath = manual
    ? `${baseUrl}/api/v1/invoice/${countryCode?.toLowerCase()}/${format}/parse`
    : `${baseUrl}/api/v1/invoice/parse`

  // Dieselbe Datei fuer beide Schritte — nur was der Nutzer abgelegt hat.
  const fileBody = JSON.stringify({
    data: fileBase64,
    ...(fileName ? { filename: fileName } : {}),
  })

  const parseResponse = await execute({
    method: 'POST',
    url: parsePath,
    headers: requestHeaders,
    body: fileBody,
  })

  const p = (await parseResponse.json().catch(() => null)) as ParseBody | null

  const fromPdf = isFromPdf(p?.infos)
  const detection =
    p?.detection ??
    (manual
      ? {
          format: p?.format ?? format ?? null,
          countryCode: countryCode ?? null,
          // Von Hand gewaehlt heisst: keine Vermutung, sondern eine Ansage.
          confidence: 100,
        }
      : undefined)

  // 🔴 Der Server hat geantwortet, nur das Lesen scheiterte (2xx mit `success: false`): kein Abbruch — die
  // Lesebefunde sind das Ergebnis. Der Abbruch bleibt dem Aufruf vorbehalten, der wirklich scheiterte
  // (Entscheid observer 10-09, Issue API 2026-10-09 validierungsseite-meldet-bei-gelesenem-misserfolg).
  if (parseResponse.ok && p !== null && p.success === false) {
    return {
      status: parseResponse.status,
      wasRead: false,
      detection,
      ...selection,
      fromPdf,
      findings: p.errors ?? [],
      warnings: p.warnings ?? [],
    }
  }

  if (!parseResponse.ok || p?.success !== true) {
    const error = errorFrom(p, parseResponse.status)
    return {
      status: parseResponse.status,
      wasRead: false,
      detection,
      ...selection,
      fromPdf,
      findings: p?.errors ?? [],
      warnings: p?.warnings ?? [],
      abort: {
        kind: abortKind(parseResponse.status, error.code),
        message: error.message,
        step: 'read',
      },
    }
  }

  // ---- Schritt 2: stimmt es? --------------------------------------------
  const country = countryCode ?? detection?.countryCode ?? undefined
  const token = format ?? catalogToken(detection?.format, country ? formatsFor(country) : [])

  if (!country || !token) {
    // Gelesen ja, pruefen nein — und die Seite sagt genau das, statt ein
    // halbes Ergebnis als ganzes auszugeben.
    return {
      status: parseResponse.status,
      wasRead: true,
      detection,
      ...selection,
      fromPdf,
      ...(p?.invoice !== undefined ? { parsed: p.invoice } : {}),
      findings: [],
      warnings: p?.warnings ?? [],
      abort: {
        kind: 'notValidatable',
        message: `Kein Prüfpfad für ${detection?.format ?? '?'} (${country ?? '?'})`,
        step: 'validate',
      },
    }
  }

  const validateResponse = await execute({
    method: 'POST',
    url: `${baseUrl}/api/v1/invoice/${country.toLowerCase()}/${token}/validate`,
    headers: requestHeaders,
    // 🔴 Die Datei selbst, nicht das daraus Gelesene: geprueft wird das Original.
    body: fileBody,
  })

  const v = (await validateResponse.json().catch(() => null)) as ValidateBody | null

  // 🔴 Auch 422 und 503 tragen einen Pruefbericht. Erst wenn gar kein Urteil
  // im Rumpf steht, ist es ein Abbruch — sonst wirft die Seite die Befunde
  // weg, wegen derer der Nutzer sie geoeffnet hat.
  const hasVerdict = Boolean(v?.status) || (v?.errors?.length ?? 0) > 0

  const rulebooks = (v?.results ?? []).map((r) => r.format)
  const skippedRulebooks = (v?.results ?? []).flatMap((r) =>
    (r.unappliedRulebooks ?? []).map((u) => ({ rulebook: u.rulebook, reason: u.reason }))
  )

  // Je Regelwerk die eigenen Befunde. `enforceFrom` traegt nur die flache Liste; die Gruppen zeigen es nicht —
  // die Warnphase gilt fuer das Dokument, nicht fuer ein einzelnes Regelwerk.
  const rulebookResults: RulebookResult[] = (v?.results ?? []).map((r) => ({
    rulebook: r.format,
    valid: r.valid ?? null,
    findings: (r.errors ?? []).map((e) => ({
      code: e.ruleId ?? e.code ?? '',
      message: e.message,
      ...(e.rule ? { rule: e.rule } : {}),
      ...(e.field ? { field: e.field } : {}),
    })),
    ...(r.skipped ? { skipped: r.skipped } : {}),
  }))

  const shared = {
    status: validateResponse.status,
    wasRead: true,
    detection,
    ...selection,
    fromPdf: fromPdf || v?.extractedFromPdf === true,
    ...(p?.invoice !== undefined ? { parsed: p.invoice } : {}),
    ...(rulebooks.length > 0 ? { rulebooks } : {}),
    ...(skippedRulebooks.length > 0 ? { skippedRulebooks } : {}),
    ...(rulebookResults.length > 0 ? { rulebookResults } : {}),
    checkedFormat: v?.format ?? token,
    findings: v?.errors ?? [],
    // Die Warnungen beider Schritte: der Parser sieht andere Dinge als das
    // Regelwerk, und beide gehoeren dem Nutzer.
    warnings: [...(p?.warnings ?? []), ...(v?.warnings ?? [])],
  }

  if (!validateResponse.ok && !hasVerdict) {
    const error = errorFrom(v, validateResponse.status)
    return {
      ...shared,
      abort: {
        kind: abortKind(validateResponse.status, error.code),
        message: error.message,
        step: 'validate',
      },
    }
  }

  return { ...shared, verdict: v?.status, reason: v?.reason }
}
